// @workflow assistant.ui-tools
/**
 * The document UI tool: `ui.documents.open { documentId, highlightTags?, page?, facilityId? }`.
 * Opens a document (a P&ID, isometric, plot plan, report…) in the facility page's document view,
 * the one the app opens P&IDs in from a tag card (forgeviewer.js launchPDFViewer, reached through
 * the viewer adapter, src/platform/viewer/documents.js), and lights the links of the given tags
 * where the document has them marked. It reports what is on screen, never what it hoped for:
 *
 *   { opened: true, document, page, pages, highlighted, notFound, onOtherPages?, notShown? }
 *   - highlighted: how many of the given tags have a lit link on the page shown (read back from the page)
 *   - notFound:    the given tags this document has no link for (attached-only tags land here)
 *   - onOtherPages: tags linked only on another page, `[{ tagId, page }]`
 *   - notShown:    tags linked on this page whose link did not light up
 *
 * The page shown is `page` when given and the document has it, else the first page with a link of
 * a given tag, else page 1. Only PDF and image files open in the document view; anything else is
 * answered not_available (an Office file would leave the chat for another pane or a new tab).
 * It needs the facility's page, loaded (PREPARE_NEED.FACILITY), like the tag tools.
 *
 * Host contract (`host.documents`, services/documentHost.js):
 *   find(documentId) → Promise<{ status: DOCUMENT_LOOKUP.*, file?: { documentId, title, fileName, storageReference } }>
 *   isDrawingFile(fileName) → boolean
 *   hasRoom() → boolean           the page has a document pane, or a free slot for one
 *   open(file) → Promise          starts loading it in the document view (resolves before it is drawn)
 *   openPage(page) → void|Promise starts loading another page of the open document
 *   view() → { documentId, loaded, model, page, pageCount, links: [{ page, tagId }], renderedTagIds }
 *   highlight(tagIds) → string[]  lights their links on the page shown; the ids now lit
 * `host.clock` ({ now, sleep(ms, signal) }) is injectable for tests, as in viewerPrepare.js.
 * No Vue or path-alias imports, so `npm run test:unit` (node --test) can load it directly.
 */
import { UI_TOOL, ok, notAvailable, failed } from './uiToolContract.js';
import { VIEWER_DETAIL, VIEWER_LIMITS, VIEWER_WAIT, sameId } from './viewerContract.js';
import { cleanFacilityId, cleanIdList } from './viewerArgs.js';
import { PREPARE_NEED, prepareViewer, SYSTEM_CLOCK, deadlineOf, progressReporter } from './viewerPrepare.js';

export const DOCUMENT_LIMITS = Object.freeze({
  MAX_ID_LENGTH: 64,
  MAX_HIGHLIGHT_TAGS: 100,
  MAX_PAGE: 10000,
});

/**
 * How long the document may take to load, and how long its links may take to be drawn once the
 * page is there (they are drawn in the same load callback; this only covers a slow frame).
 */
export const DOCUMENT_WAIT = Object.freeze({ TIMEOUT_MS: 30 * 1000, LINKS_GRACE_MS: 2000 });

/** What `host.documents.find` answers. */
export const DOCUMENT_LOOKUP = Object.freeze({ FOUND: 'found', MISSING: 'missing', DENIED: 'denied' });

export const DOCUMENT_PROGRESS = Object.freeze({ OPENING: 'Opening the document…' });

/** Plain words for the model. */
export const DOCUMENT_DETAIL = Object.freeze({
  NOT_FOUND: 'This document was not found. It may have been deleted, or it is in another facility.',
  NO_ACCESS: "The user doesn't have access to this document.",
  NOT_A_DRAWING: "This document isn't a PDF or an image, so it can't open in the document view. Give the user its link instead.",
  NOT_LOADED: 'The document took too long to open. Try again.',
});

const ID = /^[\w-]+$/;
const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
const isAbsent = (value) => value === undefined || value === null;

function cleanDocumentId(value) {
  if (typeof value !== 'string') return null;
  const id = value.trim();
  return id && id.length <= DOCUMENT_LIMITS.MAX_ID_LENGTH && ID.test(id) ? id : null;
}

/** Absent → null; a whole number 1..MAX_PAGE; anything else undefined (refused). */
function cleanPage(value) {
  if (isAbsent(value)) return null;
  return Number.isInteger(value) && value >= 1 && value <= DOCUMENT_LIMITS.MAX_PAGE ? value : undefined;
}

/** Absent or empty → []; else 1..MAX_HIGHLIGHT_TAGS clean tag ids, or null (refused). */
function cleanHighlightTags(value) {
  if (isAbsent(value) || (Array.isArray(value) && value.length === 0)) return [];
  return cleanIdList(value, { maxCount: DOCUMENT_LIMITS.MAX_HIGHLIGHT_TAGS, maxLength: VIEWER_LIMITS.MAX_TAG_ID_LENGTH });
}

/** `{ documentId, highlightTags?, page?, facilityId? }` → `{ documentId, highlightTags, page, facilityId }` or null. */
export function checkOpenDocumentArgs(args) {
  if (!isPlainObject(args)) return null;
  const documentId = cleanDocumentId(args.documentId);
  const highlightTags = cleanHighlightTags(args.highlightTags);
  const page = cleanPage(args.page);
  const facilityId = cleanFacilityId(args.facilityId);
  if (!documentId || !highlightTags || page === undefined || facilityId === undefined) return null;
  return { documentId, highlightTags, page, facilityId };
}

const key = (id) => String(id).toLowerCase();

/**
 * Where each given tag is linked in the document, and which page to show.
 * @param {Array<{ page: number|null, tagId: string }>} links every page's tag links
 * @param {string[]} tagIds the tags to light
 * @param {{ page: number|null, pageCount: number }} request `page` asked for (null: none); `pageCount` 0 when unknown
 * @returns {{ page: number, onPage: string[], onOtherPages: Array<{ tagId: string, page: number }>, notFound: string[] }}
 */
export function planDocumentPage(links, tagIds, { page = null, pageCount = 0 } = {}) {
  const pagesOf = new Map();
  (links || []).forEach((link) => {
    if (!link || !link.tagId || !link.page) return;
    const k = key(link.tagId);
    if (!pagesOf.has(k)) pagesOf.set(k, new Set());
    pagesOf.get(k).add(link.page);
  });
  const linked = tagIds.filter((id) => pagesOf.has(key(id)));
  const firstLinkedPage = linked.length ? Math.min(...linked.flatMap((id) => [...pagesOf.get(key(id))])) : null;
  const pageExists = page !== null && (!pageCount || page <= pageCount);
  const shown = (pageExists ? page : firstLinkedPage) || 1;
  const onPage = linked.filter((id) => pagesOf.get(key(id)).has(shown));
  const onOtherPages = linked
    .filter((id) => !pagesOf.get(key(id)).has(shown))
    .map((id) => ({ tagId: id, page: Math.min(...pagesOf.get(key(id))) }));
  return { page: shown, onPage, onOtherPages, notFound: tagIds.filter((id) => !pagesOf.has(key(id))) };
}

/**
 * Polls `host.documents.view()` until `test(view)` is true. Resolves `{ view }`, `{ view, timedOut }`
 * at the deadline, or `{ result }` to answer with (stopped; another document replaced this one).
 */
async function waitForView(host, documentId, test, deadline) {
  const clock = host.clock || SYSTEM_CLOCK;
  const signal = host.signal || null;
  for (;;) {
    if (signal && signal.aborted) return { result: failed(VIEWER_DETAIL.STOPPED) };
    const view = host.documents.view();
    if (view.documentId && !sameId(view.documentId, documentId)) return { result: failed(VIEWER_DETAIL.REPLACED) };
    if (test(view)) return { view };
    if (clock.now() >= deadline) return { view, timedOut: true };
    await clock.sleep(VIEWER_WAIT.POLL_MS, signal);
  }
}

/** The document is the one shown, and a page loaded after `previousModel` (null: any loaded page). */
const loadedAfter = (documentId, previousModel) => (view) =>
  sameId(view.documentId, documentId) && view.loaded && (!previousModel || view.model !== previousModel);

/** Loads the document (unless it is already on screen) and the page to show; `{ view }` or `{ result }`. */
async function showDocument(host, file, request, deadline) {
  const docs = host.documents;
  const before = docs.view();
  const alreadyOpen = loadedAfter(file.documentId, null)(before);
  if (!alreadyOpen && !docs.hasRoom()) return { result: notAvailable(VIEWER_DETAIL.NO_ROOM) };
  if (!alreadyOpen) await docs.open(file);
  let waited = await waitForView(host, file.documentId, loadedAfter(file.documentId, alreadyOpen ? null : before.model), deadline);
  if (waited.result) return waited;
  if (waited.timedOut) return { result: failed(DOCUMENT_DETAIL.NOT_LOADED) };
  const plan = planDocumentPage(waited.view.links, request.highlightTags, { page: request.page, pageCount: waited.view.pageCount });
  if (plan.page !== waited.view.page) {
    const previousModel = waited.view.model;
    await docs.openPage(plan.page);
    waited = await waitForView(host, file.documentId, (view) => view.page === plan.page && loadedAfter(file.documentId, previousModel)(view), deadline);
    if (waited.result) return waited;
    if (waited.timedOut) return { result: failed(DOCUMENT_DETAIL.NOT_LOADED) };
  }
  return { view: waited.view, plan };
}

/** Waits (briefly) for the page's links of these tags to be drawn, then lights them: `{ lit }` or `{ result }`. */
async function lightTags(host, file, tagIds) {
  if (!tagIds.length) return { lit: [] };
  const clock = host.clock || SYSTEM_CLOCK;
  const drawn = (view) => {
    const rendered = new Set((view.renderedTagIds || []).map(key));
    return tagIds.every((id) => rendered.has(key(id)));
  };
  const waited = await waitForView(host, file.documentId, drawn, clock.now() + DOCUMENT_WAIT.LINKS_GRACE_MS);
  if (waited.result) return waited;
  return { lit: host.documents.highlight(tagIds) || [] };
}

async function openDocument(input, host) {
  const docs = host.documents;
  const found = await docs.find(input.documentId);
  if (!found || found.status === DOCUMENT_LOOKUP.MISSING) return notAvailable(DOCUMENT_DETAIL.NOT_FOUND);
  if (found.status === DOCUMENT_LOOKUP.DENIED) return notAvailable(DOCUMENT_DETAIL.NO_ACCESS);
  const { file } = found;
  if (!docs.isDrawingFile(file.fileName)) return notAvailable(DOCUMENT_DETAIL.NOT_A_DRAWING);

  const report = progressReporter(host.progress);
  report(DOCUMENT_PROGRESS.OPENING);
  try {
    const clock = host.clock || SYSTEM_CLOCK;
    const shown = await showDocument(host, file, input, deadlineOf(clock.now(), host.expiresAt, DOCUMENT_WAIT.TIMEOUT_MS));
    if (shown.result) return shown.result;
    const { view, plan } = shown;
    const { lit, result } = await lightTags(host, file, plan.onPage);
    if (result) return result;
    const litKeys = new Set(lit.map(key));
    const notShown = plan.onPage.filter((id) => !litKeys.has(key(id)));
    return ok({
      opened: true,
      document: file.title || file.fileName || null,
      page: plan.page,
      pages: view.pageCount || null,
      highlighted: plan.onPage.length - notShown.length,
      notFound: plan.notFound,
      ...(plan.onOtherPages.length ? { onOtherPages: plan.onOtherPages } : {}),
      ...(notShown.length ? { notShown } : {}),
    });
  } finally {
    report(null);
  }
}

const prepareFacility = (host, input) => prepareViewer(host, input.facilityId, PREPARE_NEED.FACILITY);

/** Registry entry (uiTools.js). */
export const DOCUMENT_ACTIONS = Object.freeze({
  [UI_TOOL.DOCUMENTS_OPEN]: {
    check: checkOpenDocumentArgs,
    ready: prepareFacility,
    run: openDocument,
  },
});
